import { useQuery } from '@apollo/client'
import { useMemo, useState } from 'react'

import Flex from 'shared/ui/Flex'
import SearchInput from 'shared/ui/SearchInput'
import useAuth from 'data/gradoo/hooks/useAuth'
import useContentMarks, {
  ContentMarkType
} from 'shared/hooks/useContentMarks'
import { COLLAGES_ALBUMS } from 'data/gradoo/queries/collages'
import { CollagesAlbumFragment } from 'shared/types/gradoo/graphql'

import { PanelProps, Panels } from '../../types'
import Skeleton from './Skeleton'
import CollageItem from './components/CollageItem'

type CollagesListPanelProps = PanelProps & {
  moduleInstanceId?: string
}

const CollagesListPanel: React.FC<CollagesListPanelProps> = ({
  moduleInstanceId,
  push
}) => {
  const [search, setSearch] = useState('')
  const { authGroupId } = useAuth()

  const { markedIds, toggleMark } = useContentMarks({
    groupId: authGroupId ?? undefined,
    moduleInstanceId
  })

  const { data: collagesAlbumsData, loading } = useQuery(
    COLLAGES_ALBUMS,
    {
      skip: !moduleInstanceId,
      variables: {
        moduleInstance: moduleInstanceId,
        cpFirst: 5
      },
      context: { client: 'gradoo' }
    }
  )

  const collagesAlbums: CollagesAlbumFragment[] =
    (collagesAlbumsData?.collagesAlbums?.edges.map(
      edge => edge?.node
    ) as CollagesAlbumFragment[]) || []

  const filteredCollagesAlbums = useMemo(() => {
    return collagesAlbums.filter(ca =>
      ca.name.toLocaleLowerCase().includes(search.toLocaleLowerCase())
    )
  }, [collagesAlbums, search])

  return (
    <Flex direction="column">
      <SearchInput
        value={search}
        onChange={setSearch}
        marginBottom={26}
      />
      <Flex direction="column" rowGap={24}>
        {loading ? (
          <Skeleton />
        ) : (
          filteredCollagesAlbums.map(collagesAlbum => {
            const isMarked = markedIds.has(collagesAlbum.id)
            return (
              <CollageItem
                key={collagesAlbum.id}
                data-testid={`CollageItem:${collagesAlbum.id}`}
                onClick={() =>
                  push({
                    panel: Panels.collage,
                    title: collagesAlbum.name,
                    props: {
                      collageAlbumId: collagesAlbum.id,
                      collagesPhotoCount: collagesAlbum.photosCount
                    }
                  })
                }
                title={collagesAlbum.name}
                images={
                  collagesAlbum.collagesPhotos?.edges?.map(
                    edge => edge?.node?.photo
                  ) as string[]
                }
                isMarked={isMarked}
                onToggleMark={() =>
                  toggleMark(collagesAlbum.id, ContentMarkType.Collage)
                }
              />
            )
          })
        )}
      </Flex>
    </Flex>
  )
}

export default CollagesListPanel
